import { formatDateTime } from "../../utils/DateTime";
import ManagementPageHTML, {
  IdentityCell,
} from "../../components/ManagementPage/ManagementPageHTML";
import type {
  ManagementColumn,
  ManagementDialogField,
} from "../../components/ManagementPage/ManagementPageTypes";
import type {
  FinancialEntry,
  FinancialEntryType,
} from "../../models/FinancialEntry";
import { FinancialEntryService } from "../../services/FinancialEntryService";

const money = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
});
const columns: ManagementColumn<FinancialEntry>[] = [
  {
    Key: "Entry",
    Header: "Entry",
    Render: (row) => (
      <IdentityCell
        Icon={
          row.Type === "Income" ? "fa-arrow-trend-up" : "fa-arrow-trend-down"
        }
        Title={row.Description || row.Category}
        Subtitle={row.Code}
      />
    ),
  },
  { Key: "Category", Header: "Category", Render: (row) => row.Category },
  {
    Key: "Amount",
    Header: "Amount",
    Render: (row) => <strong>{money.format(row.Amount)}</strong>,
  },
  {
    Key: "Date",
    Header: "Transaction date",
    Render: (row) => formatDateTime(row.TransactionDate),
  },
  {
    Key: "Reference",
    Header: "Reference",
    Render: (row) => row.ReferenceCode || "—",
  },
];
export default function FinancialEntryHTML({
  Type,
}: {
  Type: FinancialEntryType;
}) {
  const fields: ManagementDialogField<FinancialEntry>[] = [
    { Key: "Code", Label: `${Type} code`, DisableAfterNew: true },
    {
      Key: "Type",
      Label: "Entry type",
      Type: "select",
      Options: [Type],
      Required: true,
    },
    { Key: "Category", Label: "Category", Required: true },
    { Key: "Amount", Label: "Amount", Type: "money", Required: true },
    { Key: "CurrencyCode", Label: "Currency", Required: true },
    { Key: "ReferenceCode", Label: "Reference" },
    {
      Key: "TransactionDate",
      Label: "Transaction date",
      Type: "datetime",
      Required: true,
    },
    {
      Key: "Description",
      Label: "Description",
      Type: "textarea",
      FullWidth: true,
    },
  ];
  return (
    <ManagementPageHTML
      Service={FinancialEntryService(Type)}
      DefaultValues={{Type,Amount:0,CurrencyCode:"NGN",TransactionDate:new Date().toISOString()}}
      CanEditRow={row => row.IsSystem !== true}
      RowFilter={(row) => row.Type === Type}
      Title={Type === "Income" ? "Income" : "Expenses"}
      Eyebrow="Financial entries"
      Description={`Create and manage ${Type.toLowerCase()} entries.`}
      AddLabel={`Add ${Type.toLowerCase()}`}
      SearchPlaceholder={`Search ${Type.toLowerCase()}`}
      Rows={[]}
      Columns={columns}
      DialogFields={fields}
      GetRowKey={(row) => row.Code}
      GetSearchText={(row) =>
        `${row.Code} ${row.Category} ${row.Description ?? ""} ${row.ReferenceCode ?? ""}`
      }
      Summaries={[]}
      Filters={[
        {
          Key: "Type",
          Label: "Entry type",
          Options: [Type],
          GetValue: (row) => row.Type,
        },
      ]}
      GetSummaries={(rows, total) => [
        {
          Label: `Total ${Type.toLowerCase()}`,
          Value: money.format(
            rows
              .filter((x) => x.Type === Type)
              .reduce((sum, x) => sum + x.Amount, 0),
          ),
          Icon: "fa-naira-sign",
          Tone: Type === "Income" ? "Green" : "Red",
        },
        { Label: "Entries", Value: total, Icon: "fa-list", Tone: "Blue" },
        {
          Label: "This page",
          Value: rows.filter((x) => x.Type === Type).length,
          Icon: "fa-file",
          Tone: "Purple",
        },
        {
          Label: "Categories",
          Value: new Set(
            rows.filter((x) => x.Type === Type).map((x) => x.Category),
          ).size,
          Icon: "fa-tags",
          Tone: "Orange",
        },
      ]}
    />
  );
}
